import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import Loading from "react-fullscreen-loading";
import Select from "react-select";
import { Button } from "react-bootstrap";
import { toast } from "react-hot-toast";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from "chart.js";
import { FaFileCsv, FaTerminal } from "react-icons/fa";

import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import DateRangeFilter from "../../../Components/DateRangeFilter/DateRangeFilter";
import CSVExportModal from "../../../Components/CSVExport/CSVExportModal";
import StatusChip from "../../../Components/StatusChip/StatusChip";
import TruncatedText, {
  TruncatedTextTooltip,
  DateCell,
  COLUMN_WIDTHS,
} from "../../../Components/TruncatedText/TruncatedText";
import { hasMetadata, MetadataDetail } from "../../../Components/CommandLogModal/CommandMetadata";
import {
  LISTINGS,
  useCustomizableColumns,
  CustomizeTableModal,
  TableCustomizerMenu,
} from "../../../Components/TableCustomizer";
import CustomLoader from "../../../utils/DataTableLoader/CustomLoader";
import ShowTime from "../../../utils/ShowTime/ShowTime";
import MapProduct from "../../../utils/ProductMapper/MapProduct";
import { Can, useAuth } from "../../../authentication/AuthContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, ChartLegend);

// The rest of the admin surfaces this same hardcoded list. Products actually
// seen in the logs are merged in at runtime (see productOptions) so a license on
// a product missing from this list is still selectable.
const BASE_PRODUCT_OPTIONS = [
  { value: "SPCAD_ACAD_Beta", label: "AUTOCAD" },
  { value: "SPCAD_CADMATE", label: "CADMATE" },
  { value: "SPCAD_GSTARCAD", label: "GSTARCAD" },
  { value: "SPCAD_BRX", label: "BRICSCAD" },
  { value: "SPCAD_ZWCAD", label: "ZWCAD" },
  { value: "SPCAD_PCAD", label: "PCAD" },
];

const TOP_N_OPTIONS = [
  { value: 10, label: "Top 10" },
  { value: 20, label: "Top 20" },
  { value: 50, label: "Top 50" },
];

// Enough distinct hues for the largest Top N; the chart cycles through them.
const CHART_COLORS = [
  "#883484", "#1976d2", "#2e7d32", "#ef6c00", "#c62828",
  "#00838f", "#6a1b9a", "#558b2f", "#ad1457", "#4527a0",
];

// ── The four ways to ask "used most" ─────────────────────────────────────────
//
// Each dimension knows how to key a row, label it on the chart, describe it in
// the table, and — the point of the whole panel — which filter clicking it
// applies. `filterColumn` names an API column, so clicking through sets an
// EXACT filter, unlike the free-text boxes which match with LIKE.
const DIMENSIONS = [
  {
    key: "commands",
    label: "Commands",
    field: "top_commands",
    countLabel: "Distinct Commands",
    rowKey: row => row.command,
    chartLabel: row => row.command,
    filterColumn: "command",
    filterValue: row => row.command,
    // Rendered in the leftmost table cell, under the clickable name.
    primary: row => row.command,
    secondary: () => null,
    extraColumns: [
      { header: "Machines", value: row => row.machine_count },
      { header: "Licenses", value: row => row.license_count },
      { header: "Users", value: row => row.user_count },
    ],
  },
  {
    key: "users",
    label: "Users",
    field: "top_users",
    countLabel: "Users",
    rowKey: row => row.user_id,
    chartLabel: row => row.user_email || `user #${row.user_id}`,
    filterColumn: "user_id",
    filterValue: row => row.user_id,
    primary: row => row.user_email || `user #${row.user_id}`,
    secondary: row => [
      [row.user_fname, row.user_lname].filter(Boolean).join(" "),
      row.user_organization,
    ].filter(Boolean).join(" · ") || null,
    extraColumns: [
      { header: "Commands", value: row => row.command_count },
      { header: "Machines", value: row => row.machine_count },
      { header: "Licenses", value: row => row.license_count },
    ],
    // A log whose license is gone has no user to attribute it to.
    unattributedLabel: "not attributable to a user (license removed)",
  },
  {
    key: "machines",
    label: "Machines",
    field: "top_machines",
    countLabel: "Machines",
    rowKey: row => row.machine_id,
    chartLabel: row => row.machine_user || row.machine_id,
    filterColumn: "machine_id",
    filterValue: row => row.machine_id,
    primary: row => row.machine_id,
    secondary: row => [
      row.machine_user || "unknown user",
      row.machine_product_version ? `v${row.machine_product_version}` : null,
      row.machine_last_seen ? `last seen ${ShowTime(row.machine_last_seen)}` : null,
    ].filter(Boolean).join(" · "),
    extraColumns: [
      { header: "Commands", value: row => row.command_count },
      { header: "Licenses", value: row => row.license_count },
      { header: "Users", value: row => row.user_count },
    ],
  },
  {
    key: "products",
    label: "Products",
    field: "top_products",
    countLabel: "Products",
    rowKey: row => row.product,
    chartLabel: row => MapProduct(row.product, "text"),
    filterColumn: "license_product",
    filterValue: row => row.product,
    primary: row => MapProduct(row.product, "text"),
    secondary: row => row.product,
    extraColumns: [
      { header: "Commands", value: row => row.command_count },
      { header: "Machines", value: row => row.machine_count },
      { header: "Users", value: row => row.user_count },
    ],
    unattributedLabel: "not attributable to a product (no license)",
  },
];

const emptySummary = {
  total_logs: 0,
  unique_commands: 0,
  unique_machines: 0,
  unique_licenses: 0,
  unique_users: 0,
  unique_products: 0,
  first_log_at: null,
  last_log_at: null,
};

const emptyBreakdowns = {
  top_commands: [],
  top_users: [],
  top_machines: [],
  top_products: [],
};

// Small headline number above the chart.
function StatTile({ label, value, hint }) {
  return (
    <div className="col-6 col-md-4 col-xl-2 mb-3">
      <div
        style={{
          border: "1px solid #e9ecef",
          borderRadius: 8,
          padding: "12px 14px",
          backgroundColor: "#fff",
          height: "100%",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#6c757d",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          {label}
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#883484", lineHeight: 1.3 }}>
          {value}
        </div>
        {hint && <div style={{ fontSize: 11, color: "#6c757d" }}>{hint}</div>}
      </div>
    </div>
  );
}

export default function CommandLogs() {
  const { can } = useAuth();
  const canSeeStats = can("admin.command_logs.stats");

  const [screenloader, setScreenloader] = useState(true);

  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("timestamp");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pending, setPending] = useState(true);

  const [summary, setSummary] = useState(emptySummary);
  const [breakdowns, setBreakdowns] = useState(emptyBreakdowns);
  const [dimensionKey, setDimensionKey] = useState(DIMENSIONS[0].key);
  const [topN, setTopN] = useState(TOP_N_OPTIONS[0]);
  const [statsPending, setStatsPending] = useState(canSeeStats);
  // The stats panel is permission-gated server-side. A 403 there must not make
  // the whole page look broken, so it hides itself instead of raising a toast.
  const [statsDenied, setStatsDenied] = useState(false);

  const [showCsvModal, setShowCsvModal] = useState(false);
  // Flipped whenever a filter changes, so the table's highlighted page
  // follows the page reset instead of pointing at a page we are not showing.
  const [resetPagination, setResetPagination] = useState(false);

  // ── Filters ────────────────────────────────────────────────────────────────
  // Column names here are the API's public names, not SQL — the server maps
  // them onto its own qualified columns.
  const initialFilters = {
    command: { operator: "", value: "" },
    machine_id: { operator: "", value: "" },
    license_reference_id: { operator: "", value: "" },
    user_email: { operator: "", value: "" },
    user_id: { operator: "", value: "" },
    license_product: { operator: "", value: "" },
    timestamp: { operator: "", value: "" },
  };

  const [filters, setFilters] = useState(initialFilters);
  const [command, setCommand] = useState("");
  const [machineId, setMachineId] = useState("");
  const [licenseRef, setLicenseRef] = useState("");
  const [email, setEmail] = useState("");
  const [product, setProduct] = useState("");
  const [timestampRange, setTimestampRange] = useState([null, null]);

  const handleFiltersChange = (key, value, operator) => {
    setFilters(prev => ({ ...prev, [key]: { operator, value } }));
    setPage(1);
    setResetPagination(prev => !prev);
  };

  const handleFiltersReset = () => {
    setFilters(initialFilters);
    setCommand("");
    setMachineId("");
    setLicenseRef("");
    setEmail("");
    setProduct("");
    setTimestampRange([null, null]);
    setPage(1);
    setResetPagination(prev => !prev);
  };

  // Each text box applies on its own — by Enter, or by the Apply affix inside the
  // field, which is the only way to submit one on a touch keyboard that has no
  // Enter. The input's `id` IS the filter column, so one handler serves them all.
  //
  // An emptied box applies a blank value, which formattedFilters drops: clearing
  // the text and applying is how you remove that one filter.
  const applyTextFilter = (id, value) => {
    handleFiltersChange(id, value === "" ? "" : `%${value}%`, "LIKE");
  };

  const handleEnterKeyPress = (e) => {
    if (e.key === "Enter") applyTextFilter(e.target.id, e.target.value);
  };

  // Filter keys whose value is mirrored by one of the text inputs. Setting or
  // clearing such a filter from anywhere else has to keep the box in step, or
  // the screen shows a filter the input contradicts.
  const inputSetters = {
    command: setCommand,
    machine_id: setMachineId,
    license_reference_id: setLicenseRef,
    user_email: setEmail,
  };

  const clearFilter = (key) => {
    handleFiltersChange(key, "", "");
    if (inputSetters[key]) inputSetters[key]("");
    if (key === "license_product") setProduct("");
    if (key === "timestamp") setTimestampRange([null, null]);
  };

  // Clicking a breakdown row narrows the whole screen — panel and listing — to
  // that one command, user, machine or product, with an EXACT match rather than
  // the text boxes' LIKE.
  const applyBreakdownFilter = (dim, row) => {
    const value = dim.filterValue(row);
    if (value === null || value === undefined) return;
    handleFiltersChange(dim.filterColumn, value, "=");

    // Keep the visible controls truthful about what is applied.
    if (inputSetters[dim.filterColumn]) inputSetters[dim.filterColumn](String(value));
    if (dim.filterColumn === "license_product") {
      setProduct({ value, label: MapProduct(String(value), "text") });
    }
  };

  // Both requests send the same filter set, so the chart always describes the
  // rows the table is showing.
  const formattedFilters = useMemo(() => (
    Object.keys(filters)
      .filter(key => {
        const value = filters[key].value;
        return Array.isArray(value) ? value.length > 0 : Boolean(value);
      })
      .map((key, index) => ({
        [`filters[${index}][column]`]: key,
        [`filters[${index}][operator]`]: filters[key].operator,
        [`filters[${index}][value]`]: filters[key].value,
      }))
      .reduce((acc, obj) => ({ ...acc, ...obj }), {})
  ), [filters]);

  const authHeaders = { "access-token": localStorage.getItem("accessToken") };

  const getCommandLogs = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/command_logs`, {
        params: {
          limit: pageSize,
          page,
          sort_by: sortBy,
          sort_order: sortOrder,
          ...formattedFilters,
        },
        headers: authHeaders,
      });

      if (response.data.status) {
        setTotalRecords(response.data.meta.pagination.total);
        setTableData(response.data.data);
      } else {
        toast.error("Something Went Wrong");
      }
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        console.error(error);
        toast.error("Something Went Wrong");
      }
    } finally {
      setScreenloader(false);
      setPending(false);
    }
  };

  const getCommandStats = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/command_logs/stats`, {
        params: { limit: topN.value, ...formattedFilters },
        headers: authHeaders,
      });

      if (response.data.status) {
        const payload = response.data.data || {};
        setSummary(payload.summary || emptySummary);
        setBreakdowns({
          top_commands: payload.top_commands || [],
          top_users:    payload.top_users || [],
          top_machines: payload.top_machines || [],
          top_products: payload.top_products || [],
        });
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setStatsDenied(true);
      } else {
        console.error(error);
      }
    } finally {
      setStatsPending(false);
    }
  };

  useEffect(() => {
    setPending(true);
    getCommandLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedFilters, page, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    if (!canSeeStats || statsDenied) return;
    setStatsPending(true);
    getCommandStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedFilters, topN, statsDenied, canSeeStats]);

  // ── Breakdown panel ───────────────────────────────────────────────────────
  const dimension = DIMENSIONS.find(d => d.key === dimensionKey) || DIMENSIONS[0];
  const rows = breakdowns[dimension.field] || [];

  // Products actually present in the logs are folded into the dropdown, so a
  // license on a product missing from the hardcoded list is still selectable.
  const productOptions = useMemo(() => {
    const seen = new Map(BASE_PRODUCT_OPTIONS.map(o => [o.value, o]));
    (breakdowns.top_products || []).forEach(row => {
      if (row.product && !seen.has(row.product)) {
        seen.set(row.product, { value: row.product, label: MapProduct(row.product, "text") });
      }
    });
    return [{ value: "", label: "All" }, ...seen.values()];
  }, [breakdowns.top_products]);

  // Horizontal bars: the labels here are commands, emails and machine ids —
  // all unreadable rotated on an x axis.
  const chartData = {
    labels: rows.map(dimension.chartLabel),
    datasets: [
      {
        label: "Commands run",
        data: rows.map(row => Number(row.usage_count) || 0),
        backgroundColor: rows.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          afterLabel: (ctx) => {
            const row = rows[ctx.dataIndex];
            if (!row) return "";
            return dimension.extraColumns.map(c => `${c.header}: ${c.value(row)}`);
          },
        },
      },
    },
    scales: {
      x: { beginAtZero: true, ticks: { precision: 0 } },
      y: { ticks: { autoSkip: false } },
    },
  };

  // Height grows with the number of bars so 50 of them stay readable.
  const chartHeight = Math.max(220, rows.length * 26 + 40);

  // A breakdown only covers rows it can attribute. Logs whose license is gone
  // have no user and no product, so those two breakdowns can sum to less than
  // total_logs — say so rather than letting the numbers look wrong.
  const attributed = rows.reduce((sum, row) => sum + (Number(row.usage_count) || 0), 0);
  const truncated = rows.length >= topN.value;
  const unattributed = dimension.unattributedLabel && !truncated
    ? Math.max(0, Number(summary.total_logs || 0) - attributed)
    : 0;

  // Filters set by clicking a breakdown row are invisible in the text boxes, so
  // every active filter gets a dismissible chip.
  const activeChips = useMemo(() => {
    const labelFor = {
      command: "Command",
      machine_id: "Machine",
      license_reference_id: "License",
      user_email: "Email",
      user_id: "User",
      license_product: "Product",
      timestamp: "Date",
    };
    return Object.keys(filters)
      .filter(key => {
        const value = filters[key].value;
        return Array.isArray(value) ? value.length > 0 : Boolean(value);
      })
      .map(key => {
        const { value, operator } = filters[key];
        let shown;
        if (operator === "BETWEEN" && Array.isArray(value)) {
          shown = `${new Date(value[0]).toLocaleDateString()} – ${new Date(value[1]).toLocaleDateString()}`;
        } else if (operator === "LIKE") {
          shown = `contains "${String(value).replace(/^%|%$/g, "")}"`;
        } else if (key === "license_product") {
          shown = MapProduct(String(value), "text");
        } else {
          shown = String(value);
        }
        return { key, label: labelFor[key] || key, shown };
      });
  }, [filters]);

  // ── Listing columns ────────────────────────────────────────────────────────
  //
  // Each carries an explicit `id`: the customiser stores columns by id, falling
  // back to the header label, so without one a reworded header would silently
  // drop the column from every saved layout.
  const allColumns = [
    {
      id: "command",
      name: "Command",
      selector: row => row.command,
      sortField: "command",
      sortable: true,
      minWidth: "200px",
      grow: 2,
      cell: row => (
        <span
          style={{
            backgroundColor: "#1976d220",
            color: "#1976d2",
            border: "1px solid #1976d240",
            borderRadius: 12,
            padding: "2px 10px",
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {row.command}
        </span>
      ),
    },
    {
      id: "machine",
      name: "Machine",
      selector: row => row.machine_id,
      sortField: "machine_id",
      sortable: true,
      minWidth: "240px",
      cell: row => (
        <div style={{ padding: "8px 0", minWidth: 0 }}>
          <div
            title={row.machine_id || ""}
            style={{
              fontFamily: "monospace",
              fontSize: 10,
              backgroundColor: "#f0f0f0",
              padding: "2px 6px",
              borderRadius: 3,
              wordBreak: "break-all",
              lineHeight: 1.3,
            }}
          >
            {row.machine_id || "-"}
          </div>
          <div style={{ fontSize: 11, color: "#6c757d", marginTop: 3 }}>
            {row.machine_user || "unknown user"}
            {row.machine_product_version ? ` · v${row.machine_product_version}` : ""}
          </div>
          {row.machine_last_seen && (
            <div style={{ fontSize: 10, color: "#adb5bd" }}>
              last seen {ShowTime(row.machine_last_seen)}
            </div>
          )}
          {row.machine_removed ? (
            <div style={{ fontSize: 10, color: "#c62828" }}>machine detached</div>
          ) : null}
        </div>
      ),
    },
    {
      id: "license",
      name: "License",
      selector: row => row.license_reference_id,
      sortField: "license_reference_id",
      sortable: true,
      minWidth: `${COLUMN_WIDTHS.refId + 40}px`,
      cell: row => {
        if (!row.license_id) {
          return <span style={{ fontSize: 12, color: "#adb5bd" }}>unassigned</span>;
        }
        // The log keeps the license it was recorded against. When the machine
        // now sits on a different one, say so rather than letting the two
        // columns quietly disagree.
        const reassigned =
          row.machine_current_license_id != null &&
          row.machine_current_license_id !== row.license_id;

        return (
          <div style={{ padding: "8px 0" }}>
            <div style={{ fontWeight: 500, fontSize: 12 }}>
              {row.license_reference_id || `#${row.license_id}`}
            </div>
            <div style={{ fontSize: 11, color: "#6c757d" }}>
              {row.license_product ? MapProduct(row.license_product, "text") : "-"}
            </div>
            {row.license_expires_at && (
              <div style={{ marginTop: 3 }}>
                <StatusChip
                  expiresAt={row.license_expires_at}
                  statusFlag={row.license_status === 1}
                />
              </div>
            )}
            {reassigned && (
              <div style={{ fontSize: 10, color: "#ef6c00", marginTop: 2 }}>
                machine now on #{row.machine_current_license_id}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "user",
      name: "User",
      selector: row => row.user_email,
      sortField: "user_email",
      sortable: true,
      minWidth: `${COLUMN_WIDTHS.email}px`,
      cell: row => (
        <div style={{ padding: "8px 0", minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 500 }}>
            {[row.user_fname, row.user_lname].filter(Boolean).join(" ") || "-"}
          </div>
          <TruncatedText text={row.user_email} maxWidth={COLUMN_WIDTHS.email} />
          {row.user_organization && (
            <div style={{ fontSize: 11, color: "#6c757d" }}>{row.user_organization}</div>
          )}
        </div>
      ),
    },
    {
      id: "timestamp",
      name: "Timestamp",
      selector: row => row.timestamp,
      sortField: "timestamp",
      sortable: true,
      minWidth: `${COLUMN_WIDTHS.date}px`,
      cell: row => <DateCell value={row.timestamp ? ShowTime(row.timestamp) : "-"} />,
    },
    {
      id: "recorded",
      name: "Recorded",
      selector: row => row.created_at,
      sortField: "created_at",
      sortable: true,
      minWidth: `${COLUMN_WIDTHS.date}px`,
      cell: row => (
        <div style={{ fontSize: 12, color: "#666" }}>
          {row.created_at ? ShowTime(row.created_at) : "-"}
        </div>
      ),
    },
  ];

  const {
    columns,
    ready: columnsReady,
    menuProps: tableMenuProps,
    customizerProps,
  } = useCustomizableColumns({
    listingKey: LISTINGS.ADMIN_COMMAND_LOGS,
    columns: allColumns,
    lockedColumnIds: ["command"],
  });

  // Only surface the expander column when a visible row actually carries
  // metadata, so the table stays clean when there's nothing extra to show.
  const anyMetadata = tableData.some(hasMetadata);

  // Flattened rows for CSV: the listing cells are JSX, and the exporter reads
  // values, so the joined fields go out as plain columns.
  const csvColumns = useMemo(() => [
    { name: "Command", selector: row => row.command },
    { name: "Machine ID", selector: row => row.machine_id },
    { name: "Machine User", selector: row => row.machine_user },
    { name: "Product Version", selector: row => row.machine_product_version },
    { name: "License Reference", selector: row => row.license_reference_id },
    { name: "License Product", selector: row => (row.license_product ? MapProduct(row.license_product, "text") : "") },
    { name: "License Expires", selector: row => (row.license_expires_at ? ShowTime(row.license_expires_at) : "") },
    { name: "User Name", selector: row => [row.user_fname, row.user_lname].filter(Boolean).join(" ") },
    { name: "User Email", selector: row => row.user_email },
    { name: "Organization", selector: row => row.user_organization },
    { name: "Timestamp", selector: row => (row.timestamp ? ShowTime(row.timestamp) : "") },
    { name: "Recorded", selector: row => (row.created_at ? ShowTime(row.created_at) : "") },
    { name: "Metadata", selector: row => (row.metadata ? JSON.stringify(row.metadata) : "") },
  ], []);

  return (
    <div>
      <AdminDashboardLayout />
      <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
      <div className="content-page-bg">
        <div className="content-page">
          <div className="content">

            <div className="row">
              <h2>Command Logs</h2>
            </div>

            <Can permission="admin.command_logs.stats">
              {!statsDenied && (
                <div className="mb-4">
                  <div className="row">
                    <StatTile label="Commands Run" value={Number(summary.total_logs || 0).toLocaleString()} />
                    <StatTile label="Distinct Commands" value={Number(summary.unique_commands || 0).toLocaleString()} />
                    <StatTile label="Users" value={Number(summary.unique_users || 0).toLocaleString()} />
                    <StatTile label="Machines" value={Number(summary.unique_machines || 0).toLocaleString()} />
                    <StatTile label="Products" value={Number(summary.unique_products || 0).toLocaleString()} />
                    <StatTile
                      label="Last Activity"
                      value={summary.last_log_at ? ShowTime(summary.last_log_at) : "-"}
                      hint={summary.first_log_at ? `since ${ShowTime(summary.first_log_at)}` : null}
                    />
                  </div>

                  <div
                    style={{
                      border: "1px solid #e9ecef",
                      borderRadius: 8,
                      padding: "16px",
                      backgroundColor: "#fff",
                    }}
                  >
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                      <h5 style={{ margin: 0 }}>
                        <FaTerminal style={{ marginRight: 8, color: "#883484" }} />
                        Most Used — by {dimension.label}
                      </h5>

                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <div className="btn-group btn-group-sm" role="group" aria-label="Breakdown dimension">
                          {DIMENSIONS.map(d => (
                            <button
                              key={d.key}
                              type="button"
                              className={`btn ${d.key === dimensionKey ? "btn-primary" : "btn-outline-secondary"}`}
                              onClick={() => setDimensionKey(d.key)}
                            >
                              {d.label}
                            </button>
                          ))}
                        </div>
                        <div style={{ minWidth: 130 }}>
                          <Select
                            classNamePrefix="rs"
                            options={TOP_N_OPTIONS}
                            value={topN}
                            onChange={setTopN}
                            isSearchable={false}
                          />
                        </div>
                      </div>
                    </div>

                    {statsPending ? (
                      <CustomLoader />
                    ) : rows.length === 0 ? (
                      <p style={{ color: "#6c757d", margin: 0 }}>
                        No command activity for the current filters.
                      </p>
                    ) : (
                      <>
                        <div style={{ height: chartHeight }}>
                          <Bar data={chartData} options={chartOptions} />
                        </div>

                        <div className="table-responsive mt-3">
                          <table className="table table-sm align-middle" style={{ marginBottom: 0 }}>
                            <thead>
                              <tr style={{ fontSize: 11, textTransform: "uppercase", color: "#6c757d" }}>
                                <th>#</th>
                                <th>{dimension.label.replace(/s$/, "")}</th>
                                <th className="text-end">Commands Run</th>
                                <th className="text-end">Share</th>
                                {dimension.extraColumns.map(c => (
                                  <th key={c.header} className="text-end">{c.header}</th>
                                ))}
                                <th>Last Used</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row, index) => {
                                const total = Number(summary.total_logs) || 0;
                                const count = Number(row.usage_count) || 0;
                                const share = total ? ((count / total) * 100).toFixed(1) : "0.0";
                                const secondary = dimension.secondary(row);
                                return (
                                  <tr key={dimension.rowKey(row)} style={{ fontSize: 12 }}>
                                    <td style={{ color: "#adb5bd" }}>{index + 1}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn btn-link p-0 text-start"
                                        style={{ fontSize: 12, fontWeight: 600, textDecoration: "none" }}
                                        title={`Filter everything by this ${dimension.label.replace(/s$/, "").toLowerCase()}`}
                                        onClick={() => applyBreakdownFilter(dimension, row)}
                                      >
                                        {dimension.primary(row)}
                                      </button>
                                      {secondary && (
                                        <div style={{ fontSize: 11, color: "#6c757d" }}>{secondary}</div>
                                      )}
                                    </td>
                                    <td className="text-end">{count.toLocaleString()}</td>
                                    <td className="text-end">{share}%</td>
                                    {dimension.extraColumns.map(c => (
                                      <td key={c.header} className="text-end">{c.value(row)}</td>
                                    ))}
                                    <td>{row.last_used_at ? ShowTime(row.last_used_at) : "-"}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {unattributed > 0 && (
                          <p style={{ fontSize: 11, color: "#6c757d", marginTop: 10, marginBottom: 0 }}>
                            {unattributed.toLocaleString()} of {Number(summary.total_logs).toLocaleString()} commands
                            are {dimension.unattributedLabel} and are not counted above.
                          </p>
                        )}
                        {truncated && (
                          <p style={{ fontSize: 11, color: "#6c757d", marginTop: 6, marginBottom: 0 }}>
                            Showing the top {topN.value} only — raise the limit to see the rest.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </Can>

            <div className="filters row">
              <div className="col-md-3 mb-3">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    id="command"
                    placeholder="Command"
                    value={command}
                    onChange={e => setCommand(e.target.value)}
                    onKeyDown={handleEnterKeyPress}
                  />
                  {command && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => applyTextFilter("command", command)}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-3 mb-3">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    id="machine_id"
                    placeholder="Machine ID"
                    value={machineId}
                    onChange={e => setMachineId(e.target.value)}
                    onKeyDown={handleEnterKeyPress}
                  />
                  {machineId && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => applyTextFilter("machine_id", machineId)}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-3 mb-3">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    id="license_reference_id"
                    placeholder="License Reference ID"
                    value={licenseRef}
                    onChange={e => setLicenseRef(e.target.value)}
                    onKeyDown={handleEnterKeyPress}
                  />
                  {licenseRef && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => applyTextFilter("license_reference_id", licenseRef)}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-3 mb-3">
                <div className="input-group">
                  <input
                    type="email"
                    className="form-control"
                    id="user_email"
                    placeholder="User Email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={handleEnterKeyPress}
                  />
                  {email && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => applyTextFilter("user_email", email)}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-3 mb-3">
                <Select
                  classNamePrefix="rs"
                  placeholder="Product"
                  options={productOptions}
                  value={product}
                  onChange={(e) => {
                    setProduct(e);
                    handleFiltersChange("license_product", e.value, "=");
                  }}
                />
              </div>

              <div className="col-md-3 mb-3">
                <DateRangeFilter
                  placeholderText="Command Date"
                  startDate={timestampRange[0]}
                  endDate={timestampRange[1]}
                  onChange={(dates) => {
                    setTimestampRange(dates);
                    const [start, end] = dates;
                    if (start && end) {
                      handleFiltersChange("timestamp", [start.toISOString(), end.toISOString()], "BETWEEN");
                    } else if (!start && !end) {
                      handleFiltersChange("timestamp", "", "");
                    }
                  }}
                />
              </div>

            </div>

            {activeChips.length > 0 && (
              <div className="row">
                <div className="col-12 mb-3 d-flex flex-wrap align-items-center gap-2">
                  <span style={{ fontSize: 11, textTransform: "uppercase", color: "#6c757d", fontWeight: 600 }}>
                    Active filters
                  </span>
                  {activeChips.map(chip => (
                    <span
                      key={chip.key}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        backgroundColor: "#88348415",
                        border: "1px solid #88348440",
                        borderRadius: 14,
                        padding: "3px 10px",
                        fontSize: 12,
                      }}
                    >
                      <span style={{ color: "#6c757d" }}>{chip.label}:</span>
                      <strong style={{ wordBreak: "break-all" }}>{chip.shown}</strong>
                      <button
                        type="button"
                        aria-label={`Remove the ${chip.label} filter`}
                        title="Remove this filter"
                        onClick={() => clearFilter(chip.key)}
                        style={{
                          border: "none",
                          background: "none",
                          padding: 0,
                          lineHeight: 1,
                          color: "#883484",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-12 col-md-6 mb-3 d-flex gap-2">
                <Button onClick={handleFiltersReset} variant="primary" className="admin-btn">
                  Clear all Filters
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowCsvModal(true)}
                  disabled={tableData.length === 0}
                  title="Export the rows currently on screen"
                >
                  <FaFileCsv style={{ marginRight: 6 }} />
                  Export CSV
                </Button>
                <TableCustomizerMenu {...tableMenuProps} />
              </div>
              <div className="col-12 col-md-6 mb-3 text-md-end">
                <p style={{ margin: 0 }}>
                  Total Records: <strong>{Number(totalRecords || 0).toLocaleString()}</strong>
                </p>
              </div>
            </div>

            <div className="table-wrapper">
              {!columnsReady ? (
                <CustomLoader />
              ) : (
              <DataTable
                columns={columns}
                data={tableData}
                dense
                direction="auto"
                highlightOnHover
                noHeader
                persistTableHead
                pointerOnHover
                responsive
                subHeaderAlign="right"
                subHeaderWrap
                progressPending={pending}
                progressComponent={<CustomLoader />}
                expandableRows={anyMetadata}
                expandableRowDisabled={row => !hasMetadata(row)}
                expandableRowsComponent={MetadataDetail}
                pagination
                paginationServer
                paginationPerPage={pageSize}
                paginationTotalRows={totalRecords}
                paginationResetDefaultPage={resetPagination}
                paginationRowsPerPageOptions={[10, 20, 50, 100, 150, 500]}
                onChangePage={p => setPage(p)}
                onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
                  setPageSize(currentRowsPerPage);
                  setPage(currentPage);
                }}
                sortServer
                onSort={(column, sortDirection) => {
                  setSortBy(column.sortField);
                  setSortOrder(sortDirection);
                }}
              />
              )}
            </div>
          </div>
        </div>
      </div>

      <CustomizeTableModal {...customizerProps} title="Customize Command Logs Table" />

      <CSVExportModal
        show={showCsvModal}
        onHide={() => setShowCsvModal(false)}
        columns={csvColumns}
        rows={tableData}
        filename="command-logs.csv"
      />

      <TruncatedTextTooltip />
    </div>
  );
}
